import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import { useTutorial } from '../contexts/TutorialContext';
import TutorialWizard from '../components/tutorial/TutorialWizard';

export default function PublicDashboard() {
  const { user, isAdmin, isLecturer, isStudent } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const { startTutorial, resetTutorial } = useTutorial();

  useEffect(() => {
    loadPublicData();
  }, []);

  // Redirect to appropriate dashboard if user is logged in
  useEffect(() => {
    // Only redirect if user exists and we're not already on a dashboard
    if (user && !location.pathname.startsWith('/dashboard')) {
      if (isAdmin()) {
        navigate('/dashboard/admin/dashboard', { replace: true });
      } else if (isLecturer()) {
        navigate('/dashboard/lecturer/dashboard', { replace: true });
      } else if (isStudent()) {
        navigate('/dashboard/student/dashboard', { replace: true });
      }
    }
  }, [user, location.pathname, navigate, isAdmin, isLecturer, isStudent]);

  useEffect(() => {
    // Start tutorial after data loads (only if not skipped/completed)
    if (!loading && !error && data) {
      // Check if tutorial was already skipped or completed
      const tutorialSkipped = localStorage.getItem('tutorial_skipped_/');
      const tutorialCompleted = localStorage.getItem('tutorial_completed_/');

      if (!tutorialSkipped && !tutorialCompleted) {
        setTimeout(() => {
          const tutorialSteps = [
            {
              target: '.statistics-cards',
              title: '📊 Statistik Overview',
              content: 'Ini adalah statistik keseluruhan progress semua kelompok. Anda bisa melihat berapa kelompok yang on track, at risk, atau behind.',
              position: 'bottom'
            },
            {
              target: 'input[placeholder*="Search"]',
              title: '🔍 Cari Mahasiswa',
              content: 'Ketik nama atau NIM mahasiswa di sini untuk mencari kelompok dan progress mereka. Support multi-course student!',
              position: 'bottom'
            },
            {
              target: 'button[class*="dropdown"]',
              title: '📚 Filter Course',
              content: 'Klik untuk filter kelompok berdasarkan course. Pilih "All Courses" untuk lihat semua atau pilih course spesifik.',
              position: 'left'
            },
            {
              target: '.group-card',
              title: '👥 Progress Kelompok',
              content: 'Setiap kartu menampilkan kelompok dengan progress bar, status badge, topic, dan members. Scroll ke bawah untuk melihat semua kelompok.',
              position: 'top'
            },
            {
              target: 'button[title="Tutorial"]',
              title: '❓ Butuh Bantuan?',
              content: 'Klik tombol ? di pojok kanan atas untuk melihat tutorial ini kembali lagi kapan saja. Selamat memantau progress!',
              position: 'left'
            }
          ];
          startTutorial(tutorialSteps, '/');
        }, 1500);
      }
    }
  }, [loading, error, data]);

  // Tutorial handler with steps
  const handleResetTutorial = () => {
    const tutorialSteps = [
      {
        target: '.statistics-cards',
        title: '📊 Statistik Overview',
        content: 'Ini adalah statistik keseluruhan progress semua kelompok. Anda bisa melihat berapa kelompok yang on track, at risk, atau behind.',
        position: 'bottom'
      },
      {
        target: 'input[placeholder*="Search"]',
        title: '🔍 Cari Mahasiswa',
        content: 'Ketik nama atau NIM mahasiswa di sini untuk mencari kelompok dan progress mereka. Support multi-course student!',
        position: 'bottom'
      },
      {
        target: 'button[class*="dropdown"]',
        title: '📚 Filter Course',
        content: 'Klik untuk filter kelompok berdasarkan course. Pilih "All Courses" untuk lihat semua atau pilih course spesifik.',
        position: 'left'
      },
      {
        target: '.group-card',
        title: '👥 Progress Kelompok',
        content: 'Setiap kartu menampilkan kelompok dengan progress bar, status badge, topic, dan members. Scroll ke bawah untuk melihat semua kelompok.',
        position: 'top'
      },
      {
        target: 'button[title="Tutorial"]',
        title: '❓ Butuh Bantuan?',
        content: 'Klik tombol ? di pojok kanan atas untuk melihat tutorial ini kembali lagi kapan saja. Selamat memantau progress!',
        position: 'left'
      }
    ];
    resetTutorial(tutorialSteps, '/');
  };

  const loadPublicData = async () => {
    try {
      setLoading(true);
      const API_URL = import.meta.env.VITE_API_URL || 'https://progress-tracker-api.syafri-tuloli.workers.dev/api';
      const response = await fetch(`${API_URL}/public/status`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.error || 'Failed to load data');
      }
    } catch (err) {
      console.error('Error loading public data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (!query || query.length < 2) {
      setSearchResults(null);
      return;
    }

    try {
      setSearching(true);
      const API_URL = import.meta.env.VITE_API_URL || 'https://progress-tracker-api.syafri-tuloli.workers.dev/api';
      const response = await fetch(`${API_URL}/public/search?q=${encodeURIComponent(query)}`);
      const result = await response.json();

      if (result.success) {
        setSearchResults(result.data);
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadPublicData}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.semester) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Active Semester</h2>
          <p className="text-gray-600">There is currently no active semester. Please contact admin.</p>
        </div>
      </div>
    );
  }

  const courses = data.courses || [];
  const allGroups = data.groups || [];
  const stats = data.statistics || {};

  // Filter groups by selected course
  const displayGroups = selectedCourse
    ? allGroups.filter(g => g.course_id === selectedCourse.id)
    : allGroups;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gradient">
                Student Progress Tracker
              </h1>
              <p className="text-gray-600">
                {data.semester.name} ({data.semester.year})
              </p>
            </div>

            <div className="flex items-center gap-3">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by name..."
                  className="input pl-10 pr-10"
                  style={{ minWidth: '200px' }}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0014 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-3 3m0 0l-3 3m3-3v12" />
                  </svg>
                </div>
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute inset-y-0 right-0 pr-3 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Course Filter */}
              <div className="relative">
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2v5a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2z" />
                  </svg>
                  <span>
                    {selectedCourse ? selectedCourse.name : `All Courses (${courses.length})`}
                  </span>
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border z-50">
                    <div className="p-2">
                      <button
                        onClick={() => {
                          setSelectedCourse(null);
                          setShowDropdown(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg block"
                      >
                        All Courses ({allGroups.length} groups)
                      </button>
                      {courses.map((course) => (
                        <button
                          key={course.id}
                          onClick={() => {
                            setSelectedCourse(course);
                            setShowDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 rounded-lg block"
                        >
                          {course.code} - {course.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Login Button - only show if not logged in */}
              {!user ? (
                <a
                  href="/login"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Login
                </a>
              ) : (
                /* User info - only show if logged in */
                <div className="flex items-center gap-2 text-sm text-gray-700">
                  <span className="font-medium">{user.name}</span>
                  <a
                    href="/dashboard"
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-gray-700 transition"
                  >
                    Dashboard
                  </a>
                </div>
              )}

              {/* Help Button */}
              <button
                onClick={() => handleResetTutorial()}
                className="px-3 py-2 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition"
                title="Tutorial"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search Results */}
        {searchResults && (
          <div className="mb-8 bg-white rounded-2xl shadow-lg p-6 animate-fade-in-up">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Search Results for "{searchQuery}" ({searchResults.count} found)
            </h2>
            {searchResults.results.length === 0 ? (
              <p className="text-gray-600">No students found matching your search.</p>
            ) : (
              <div className="space-y-4">
                {searchResults.results.map((result, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-bold text-gray-900">{result.name}</span>
                      {result.nim && (
                        <span className="text-sm text-gray-600">({result.nim})</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {result.courses.map((course) => (
                        <div key={course.id} className="ml-4">
                          <div className="text-sm font-semibold text-gray-800">
                            📚 {course.code} - {course.name}
                          </div>
                          {course.groups.length > 0 ? (
                            <div className="ml-6 space-y-1">
                              {course.groups.map((group) => (
                                <div key={group.id} className="text-sm text-gray-600">
                                  Group: {group.name} • Progress: {group.progress.submitted}/{group.progress.total} ({Math.round((group.progress.submitted / group.progress.total) * 100)}%)
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="ml-6 text-sm text-gray-500 italic">
                              No group yet
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button
              onClick={clearSearch}
              className="mt-4 text-sm text-blue-600 hover:underline"
            >
              ← Clear search
            </button>
          </div>
        )}

        {/* Statistics Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8 statistics-cards">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="text-gray-500 text-sm mb-1">Total Groups</div>
            <div className="text-3xl font-bold text-blue-600">{stats.total_groups}</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="text-gray-500 text-sm mb-1">On Track</div>
            <div className="text-3xl font-bold text-green-600">{stats.on_track}</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="text-gray-500 text-sm mb-1">At Risk</div>
            <div className="text-3xl font-bold text-yellow-600">{stats.at_risk}</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="text-gray-500 text-sm mb-1">Behind</div>
            <div className="text-3xl font-bold text-red-600">{stats.behind}</div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="text-gray-500 text-sm mb-1">Completion</div>
            <div className="text-3xl font-bold text-purple-600">{stats.completion_rate}%</div>
          </div>
        </div>

        {/* Groups Grid */}
        {displayGroups.length === 0 ? (
          <div className="bg-white rounded-xl shadow-md p-12 text-center">
            <p className="text-gray-500">No groups found for the selected course.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayGroups.map((group) => (
              <div key={group.id} className="card hover-lift group-card">
                {/* Progress Bar */}
                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700">Progress</span>
                    <span className="text-sm font-bold text-gray-900">{group.progress}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${group.progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="mb-4">
                  {group.status === 'on_track' && (
                    <span className="badge badge-success">On Track</span>
                  )}
                  {group.status === 'at_risk' && (
                    <span className="badge badge-warning">At Risk</span>
                  )}
                  {group.status === 'behind' && (
                    <span className="badge badge-error">Behind</span>
                  )}
                </div>

                {/* Group Name */}
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {group.group_name}
                </h3>

                {/* Topic */}
                {group.topic_title && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-600">{group.topic_title}</p>
                  </div>
                )}

                {/* Members */}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Members:</p>
                  <div className="space-y-1">
                    {group.members.map((member, idx) => (
                      <div key={idx} className="text-sm text-gray-700">
                        {member.name} {member.nim && `(${member.nim})`}
                        {member.role === 'leader' && (
                          <span className="ml-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                            Leader
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Milestone Stats */}
                <div className="text-xs text-gray-500">
                  {group.approved_count}/{group.submitted_count} approved • {group.pending_count} pending
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="max-w-7xl mx-auto px-4 py-6 text-center">
        <p className="text-gray-500 text-sm">
          © 2026 Student Progress Tracker. Manajemen Proyek Sistem Informasi - Universitas Negeri Gorontalo
        </p>
        <p className="text-gray-400 text-xs mt-1">
          Need access? <a href="/login" className="text-blue-600 hover:underline">Login</a>
        </p>
      </footer>

      {/* Tutorial Wizard */}
      <TutorialWizard />
    </div>
  );
}
